import { useState, type FormEvent } from "react";
import { RefreshCw } from "lucide-react";
import { FilterSelect, Toolbar } from "../../ui/Toolbar";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AGENT_TOOLS,
  DEFAULT_AGENT_BUDGET,
  listAgentChannels,
  bindAgentChannel,
  unbindAgentChannel,
  type AgentToolName,
  type CompletionCriterion,
  type CreateResponsibility,
  type Responsibility,
  type ResponsibilityDetail,
  type AgentDeliveryReview,
  type AgentCustomerReview,
} from "../../api/hub/project-agents";
import { listTasks } from "../../api/hub/work";
import { listApprovals } from "../../api/hub/collaboration";
import { undoAiAction } from "../../api/hub/ai";
import { useProjectAgents, agentStatus } from "./useProjectAgents";
import "./project-agents.css";
import { toolLabels, reversibleAgentTools } from "./labels";
const criterionLabels: Record<CompletionCriterion["kind"], string> = {
  human_confirmed: "由人确认完成",
  task_done: "指定任务已完成",
  approval_approved: "指定审批已批准",
  artifact_exists: "历史草稿条件（只读）",
  progress_written: "已有 AI 项目进展",
};
const time = (value: number | null) =>
  value ? new Date(value).toLocaleString("zh-CN") : "暂无安排";
const safeUrl = (url: string) => /^(https?:\/\/|\/(?!\/))/.test(url);
export function ProjectAgentsPage() {
  const navigate = useNavigate(),
    route = useParams(),
    [params, setParams] = useSearchParams();
  const id = route.id || params.get("id") || "",
    projectId = params.get("project") || "";
  const stepOffset = Math.max(0, Number(params.get("stepsOffset") || 0) || 0);
  const work = useProjectAgents(id, projectId || undefined, stepOffset);
  const [status, setStatus] = useState(""),
    [creating, setCreating] = useState(false),
    [editing, setEditing] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false);
  const perform = async (fn: () => Promise<unknown>, message = "已保存") => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
      setNotice(message);
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const detail = work.detail.data;
  return (
    <section className="work-page project-agents">
      <header className="work-heading">
        <h1>项目管家</h1>
        <button
          className="work-primary"
          onClick={() => {
            setCreating(true);
            setEditing(false);
          }}
        >
          新建职责
        </button>
      </header>
      <Toolbar
        label="筛选职责"
        end={
          <button className="lz-tool-btn" onClick={() => void work.list.refetch()}>
            <RefreshCw aria-hidden="true" />
            刷新列表
          </button>
        }
      >
        <FilterSelect
          label="所属项目"
          wide
          value={projectId}
          onChange={(v) => {
            const next = new URLSearchParams(params);
            v ? next.set("project", v) : next.delete("project");
            setParams(next);
          }}
        >
          {work.projects.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect label="职责状态" value={status} onChange={setStatus}>
          {Object.entries(agentStatus).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </FilterSelect>
      </Toolbar>
      {(error || work.list.error || work.projects.error) && (
        <p role="alert" className="work-error">
          {error || work.list.error?.message || work.projects.error?.message}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {work.list.isPending ? (
        <p role="status">正在加载职责…</p>
      ) : (
        <div className="work-grid">
          {(work.list.data || [])
            .filter((r) => !status || r.status === status)
            .map((r) => (
              <Link
                className="pa-card"
                key={r.id}
                to={`/project-agents/${encodeURIComponent(r.id)}`}
              >
                <span className="pa-status">{agentStatus[r.status]}</span>
                <h2>{r.title}</h2>
                <p>{r.goal}</p>
                <p className="work-meta">
                  {work.projects.data?.find((p) => p.id === r.projectId)
                    ?.title || "项目"}{" "}
                  · 下一检查 {time(r.nextWakeAt)}
                </p>
                <p>{r.lastSummary || "等待第一次检查"}</p>
              </Link>
            ))}
          {!work.list.isError &&
            !(work.list.data || []).some(
              (r) => !status || r.status === status,
            ) && (
              <p className="work-empty">
                暂无符合条件的职责。选定项目后，新建一个需要持续跟进的目标。
              </p>
            )}
        </div>
      )}
      {id && (
        <section className="pa-detail" aria-label="职责详情">
          <div className="work-heading">
            <h2>职责详情</h2>
            <Link
              to={
                projectId
                  ? `/project-agents?project=${encodeURIComponent(projectId)}`
                  : "/project-agents"
              }
            >
              返回列表
            </Link>
          </div>
          {work.detail.isPending ? (
            <p role="status">正在加载详情…</p>
          ) : work.detail.error ? (
            <p role="alert">
              {work.detail.error.message}{" "}
              <button onClick={() => void work.detail.refetch()}>重试</button>
            </p>
          ) : (
            detail && (
              <>
                <header>
                  <h2>{detail.responsibility.title}</h2>
                  <p>
                    <strong>{agentStatus[detail.responsibility.status]}</strong>{" "}
                    · 下一检查 {time(detail.responsibility.nextWakeAt)}
                  </p>
                  <p className="pa-prose">{detail.responsibility.goal}</p>
                  <p>{detail.responsibility.lastSummary || "尚无检查摘要"}</p>
                </header>
                {detail.canManage && (
                  <div className="work-toolbar">
                    <button disabled={busy} onClick={() => setEditing(true)}>
                      编辑职责
                    </button>
                    <button
                      disabled={
                        busy ||
                        ["completed", "paused"].includes(
                          detail.responsibility.status,
                        )
                      }
                      onClick={() =>
                        void perform(
                          () =>
                            work.change.mutateAsync({ id, action: "pause" }),
                          "已暂停职责",
                        )
                      }
                    >
                      暂停
                    </button>
                    <button
                      disabled={
                        busy ||
                        !["paused", "error", "budget_exhausted"].includes(
                          detail.responsibility.status,
                        )
                      }
                      onClick={() =>
                        void perform(
                          () =>
                            work.change.mutateAsync({ id, action: "resume" }),
                          "已恢复职责",
                        )
                      }
                    >
                      恢复
                    </button>
                    <button
                      disabled={
                        busy ||
                        ["completed", "paused"].includes(
                          detail.responsibility.status,
                        )
                      }
                      onClick={() =>
                        void perform(
                          () => work.change.mutateAsync({ id, action: "run" }),
                          "已排队检查，请稍后刷新查看结果",
                        )
                      }
                    >
                      立即检查
                    </button>
                  </div>
                )}
                <AgentDetail
                  detail={detail}
                  deliveryReviews={work.deliveryReviews.data || []}
                  deliveryError={work.deliveryReviews.error?.message}
                  customerReviews={work.customerReviews.data || []}
                  customerReviewError={work.customerReviews.error?.message}
                  busy={busy}
                  stepOffset={stepOffset}
                  onStepPage={(next) => setParams((previous) => {
                    const updated = new URLSearchParams(previous);
                    if (next <= 0) updated.delete("stepsOffset"); else updated.set("stepsOffset", String(next));
                    return updated;
                  })}
                  onSource={(content, title, url) =>
                    perform(
                      () =>
                        work.source.mutateAsync({
                          id,
                          source: {
                            kind: "manual",
                            externalId: crypto.randomUUID(),
                            title,
                            content,
                            url,
                          },
                        }),
                      "已补充输入",
                    )
                  }
                  onDecision={(decisionId, answer) =>
                    perform(
                      () => {
                        const decision = detail.decisions.find(d => d.id === decisionId);
                        const optionId = decision?.optionIds?.[decision.options.indexOf(answer)];
                        if (!decision?.version || !optionId) throw new Error("决定已变化，请刷新后重新选择");
                        return work.decision.mutateAsync({ id, decisionId, answer, version: decision.version, optionId });
                      },
                      "已提交人工决定",
                    )
                  }
                  onConfirm={(criterionId, evidence) =>
                    perform(
                      () =>
                        work.confirm.mutateAsync({ id, criterionId, evidence }),
                      "已记录完成证据",
                    )
                  }
                  onUndo={(actionId) =>
                    perform(() => undoAiAction(actionId), "已提交撤销")
                  }
                  onVerifyDelivery={(review, messageId) =>
                    perform(
                      () => work.verifyDelivery.mutateAsync({ id, review, messageId }),
                      "已读取远端消息并核验送达，不会重新发送",
                    )
                  }
                  onCustomerReview={(review, decision, edited) =>
                    perform(
                      () => work.decideCustomerReview.mutateAsync({ id, reviewId: review.id, decision, edited }),
                      decision === "publish" ? "已发布审阅内容" : decision === "reconcile" ? "已核对发布结果" : "已驳回待审内容",
                    )
                  }
                />
              </>
            )
          )}
        </section>
      )}
      {(creating || (editing && detail?.canManage)) && (
        <AgentForm
          key={creating ? "new" : id}
          responsibility={creating ? undefined : detail?.responsibility}
          projects={work.projects.data || []}
          artifacts={creating ? [] : detail?.artifacts || []}
          initialProject={projectId}
          busy={busy}
          onCancel={() => {
            setCreating(false);
            setEditing(false);
          }}
          onSave={(input) =>
            perform(async () => {
              if (creating) {
                const result = await work.create.mutateAsync(input);
                navigate(`/project-agents/${encodeURIComponent(result.id)}`);
              } else if (detail) {
                const { projectId: _project, ...patch } = input;
                await work.update.mutateAsync({
                  id,
                  patch: {
                    ...patch,
                    expectedRevision: detail.responsibility.revision,
                  },
                });
              }
              setCreating(false);
              setEditing(false);
            }, "职责已保存")
          }
        />
      )}
    </section>
  );
}
function AgentForm({
  responsibility: r,
  projects,
  artifacts,
  initialProject,
  busy,
  onCancel,
  onSave,
}: {
  responsibility?: Responsibility;
  projects: { id: string; title: string }[];
  artifacts: { id: string; title: string }[];
  initialProject: string;
  busy: boolean;
  onCancel: () => void;
  onSave: (input: CreateResponsibility) => Promise<unknown>;
}) {
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [projectId, setProject] = useState(
      r?.projectId || initialProject || projects[0]?.id || "",
    ),
    [title, setTitle] = useState(r?.title || ""),
    [goal, setGoal] = useState(r?.goal || ""),
    [criteria, setCriteria] = useState<CompletionCriterion[]>(
      r?.completionCriteria || [
        { id: crypto.randomUUID(), kind: "human_confirmed", description: "" },
      ],
    ),
    [tools, setTools] = useState<AgentToolName[]>(
      r ? r.allowedTools.filter(t => AGENT_TOOLS.includes(t)) : [...AGENT_TOOLS],
    ),
    [budget, setBudget] = useState({ ...DEFAULT_AGENT_BUDGET, ...r?.budget, maxCostUsdPerRun: r?.budget.maxCostUsdPerRun ?? 1 }),
    [channelId, setChannel] = useState(r?.channelId || ""),
    [chatId, setChat] = useState(""),
    [binding, setBinding] = useState(false),
    [unbinding, setUnbinding] = useState(false),
    [bindingError, setBindingError] = useState("");
  const channels = useQuery({
    queryKey: ["hub", "project-agent-channels", projectId],
    queryFn: () => listAgentChannels(projectId),
    enabled: !!projectId,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (criteria.some(c => c.kind === "artifact_exists")) return;
    void onSave({
      projectId,
      ...(!r ? { idempotencyKey } : {}),
      title: title.trim(),
      goal: goal.trim(),
      completionCriteria: criteria.map((c) => ({
        ...c,
        description: c.description.trim(),
      })),
      allowedTools: tools,
      budget,
      channelId: channelId || null,
    });
  };
  return (
    <section className="pa-form-panel" aria-label={r ? "编辑职责" : "新建职责"}>
      <header className="work-heading">
        <h2>{r ? "编辑职责" : "新建职责"}</h2>
        <button disabled={busy} onClick={onCancel}>
          取消
        </button>
      </header>
      <p>
        只授予数据监测职责。管家不创建或修改任务、不生成草稿、不当任务负责人。需要取舍时提交待决定。
      </p>
      <form className="work-form" onSubmit={submit}>
        <label>
          所属项目
          <select
            required
            disabled={!!r}
            value={projectId}
            onChange={(e) => {
              setProject(e.target.value);
              setChannel("");
            }}
          >
            <option value="">请选择项目</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          职责名称
          <input
            autoFocus
            required
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label>
          监测目标
          <textarea
            required
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="希望完成什么？哪些事项需要持续跟进？"
          />
        </label>
        <fieldset>
          <legend>完成条件</legend>
          {criteria.some(c => c.kind === "artifact_exists") && <p role="note">旧草稿条件仅供历史核对。修改职责前，请改为 AI 项目进展或人工确认等现行条件。</p>}
          {criteria.map((c, i) => (
            <div className="pa-criterion" key={c.id}>
              <label>
                条件 {i + 1} 类型
                <select
                  value={c.kind}
                  onChange={(e) =>
                    setCriteria((cs) =>
                      cs.map((x) =>
                        x.id === c.id
                          ? {
                              ...x,
                              kind: e.target
                                .value as CompletionCriterion["kind"],
                              entityId: undefined,
                            }
                          : x,
                      ),
                    )
                  }
                >
                  {c.kind === "artifact_exists" && <option value="artifact_exists" disabled>历史草稿条件，请改选新条件</option>}
                  {Object.entries(criterionLabels).filter(([k]) => k !== "artifact_exists").map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                条件 {i + 1} 说明
                <input
                  required
                  value={c.description}
                  onChange={(e) =>
                    setCriteria((cs) =>
                      cs.map((x) =>
                        x.id === c.id
                          ? { ...x, description: e.target.value }
                          : x,
                      ),
                    )
                  }
                />
              </label>
              {c.kind === "progress_written" && <label>项目进展编号（可选）<input aria-label="项目进展编号（可选）" value={c.entityId || ""} onChange={e => setCriteria(cs => cs.map(x => x.id === c.id ? { ...x, entityId: e.target.value || undefined } : x))}/><small>留空表示本项目已有任意带 AI 生成标记的进展。</small></label>}
              {c.kind !== "human_confirmed" && c.kind !== "progress_written" && c.kind !== "artifact_exists" && (
                <CriterionEntity
                  kind={c.kind}
                  projectId={projectId}
                  artifacts={artifacts}
                  value={c.entityId || ""}
                  onChange={(entityId) =>
                    setCriteria((cs) =>
                      cs.map((x) => (x.id === c.id ? { ...x, entityId } : x)),
                    )
                  }
                />
              )}
              {criteria.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setCriteria((cs) => cs.filter((x) => x.id !== c.id))
                  }
                >
                  移除条件 {i + 1}
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setCriteria((cs) => [
                ...cs,
                {
                  id: crypto.randomUUID(),
                  kind: "human_confirmed",
                  description: "",
                },
              ])
            }
          >
            添加完成条件
          </button>
        </fieldset>
        <fieldset>
          <legend>允许的内部动作</legend>
          <div className="pa-tools">
            {AGENT_TOOLS.map((tool) => (
              <label key={tool}>
                <input
                  type="checkbox"
                  checked={tools.includes(tool)}
                  onChange={(e) =>
                    setTools((ts) =>
                      e.target.checked
                        ? [...ts, tool]
                        : ts.filter((t) => t !== tool),
                    )
                  }
                />
                {toolLabels[tool]}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>检查频率与行动上限</legend>
          <div className="work-form-grid">
            {(
              [
                ["minWakeMinutes", "最短检查间隔（分钟）"],
                ["maxRunsPerDay", "每日最多检查次数"],
                ["maxStepsPerRun", "每次最多行动步数"],
                ["maxWritesPerRun", "每次最多写入次数"],
              ] as const
            ).map(([key, label]) => (
              <label key={key}>
                {label}
                <input
                  required
                  type="number"
                  min={1}
                  max={
                    key === "minWakeMinutes"
                      ? 525600
                      : key === "maxRunsPerDay"
                        ? 1000
                        : 100
                  }
                  step={1}
                  value={budget[key]}
                  onChange={(e) =>
                    setBudget((b) => ({ ...b, [key]: Number(e.target.value) }))
                  }
                />
              </label>
            ))}
          </div>
          <label>
            每次模型费用上限（美元）
            <input required type="number" min={0.01} max={100} step={0.01}
              value={budget.maxCostUsdPerRun}
              onChange={(e) => setBudget((b) => ({ ...b, maxCostUsdPerRun: Number(e.target.value) }))}
              aria-describedby="pa-cost-hint" />
          </label>
          <p className="work-meta" id="pa-cost-hint">默认每次 1 美元，最多 100 美元。用于限制本次模型运行费用；用量回报可能延迟，不能承诺精确到分的硬截断。</p>
          <details>
            <summary>高级资源限制</summary>
            <label>
              每次最大模型用量
              <input
                required
                type="number"
                min={1}
                max={1000000}
                step={1}
                value={budget.maxTokensPerRun}
                onChange={(e) =>
                  setBudget((b) => ({
                    ...b,
                    maxTokensPerRun: Number(e.target.value),
                  }))
                }
              />
            </label>
            <p className="work-meta">模型用量是 token（模型计量单位）的观察阈值，运行后按回报核对，并非预付额度。费用与最大轮数同时限制模型运行。</p>
          </details>
        </fieldset>
        <details>
          <summary>绑定内部讨论群（可选）</summary>
          <p className="work-meta">
            只可使用已验证的本项目内部群。绑定权限由服务器检查。
          </p>
          <label>
            已验证讨论群
            <select
              value={channelId}
              onChange={(e) => setChannel(e.target.value)}
            >
              <option value="">不绑定讨论群</option>
              {channels.data?.map((c) => (
                <option key={c.chatId} value={c.chatId}>
                  {c.chatId}
                </option>
              ))}
            </select>
          </label>
          {channels.isPending && projectId && (
            <p role="status">正在读取群配置…</p>
          )}
          {channels.error && <p role="alert">{channels.error.message}</p>}
          <label>
            新增群 ID
            <input value={chatId} onChange={(e) => setChat(e.target.value)} />
          </label>
          <button
            type="button"
            disabled={binding || !projectId || !chatId.trim()}
            onClick={async () => {
              setBinding(true);
              setBindingError("");
              try {
                const c = await bindAgentChannel(projectId, chatId.trim());
                await channels.refetch();
                setChannel(c.chatId);
                setChat("");
              } catch (e) {
                setBindingError((e as Error).message);
              } finally {
                setBinding(false);
              }
            }}
          >
            {binding ? "正在验证…" : "验证并绑定群"}
          </button>
          <button
            type="button"
            disabled={unbinding || !channelId}
            onClick={async () => {
              setUnbinding(true);
              setBindingError("");
              try {
                const result = await unbindAgentChannel(channelId);
                setChannel("");
                await channels.refetch();
                setBindingError(`群已解绑；${result.affectedResponsibilities} 项关联职责已暂停，请刷新后查看。`);
              } catch (e) {
                setBindingError((e as Error).message);
              } finally {
                setUnbinding(false);
              }
            }}
          >
            {unbinding ? "正在解绑…" : "解绑所选讨论群"}
          </button>
          {bindingError && <p role="alert">{bindingError}</p>}
        </details>
        <button
          className="work-primary"
          disabled={
            busy ||
            binding ||
            !projectId ||
            !title.trim() ||
            !goal.trim() ||
            criteria.some((c) => !c.description.trim() || c.kind === "artifact_exists")
          }
        >
          {busy ? "正在保存…" : r ? "保存职责" : "创建职责"}
        </button>
      </form>
    </section>
  );
}
function CustomerReviewCard({ review, busy, canManage, onDecide }: {
  review: AgentCustomerReview;
  busy: boolean;
  canManage: boolean;
  onDecide: (decision: "publish" | "reject" | "reconcile", edited: Record<string, string>) => Promise<unknown>;
}) {
  const [title, setTitle] = useState(review.proposal.title || "");
  const [description, setDescription] = useState(review.proposal.description || "");
  const [content, setContent] = useState(review.proposal.content || "");
  const [health, setHealth] = useState(review.proposal.health || "on_track");
  const kind = {
    task_content: "任务标题与描述",
    task_comment: "任务评论",
    project_progress: "项目进展",
  }[review.kind];
  return <article className="pa-record">
    <h4>{kind} · {review.status === "ready" ? "待审阅" : review.status === "published" ? "已发布" : review.status === "rejected" ? "已驳回" : "待核查"}</h4>
    <p className="work-meta">{time(review.createdAt)} · 关联事项 {review.entityId}</p>
    {review.kind === "task_content" && review.status === "ready" && <p className="work-meta">当前公开内容：{review.publicValue.title || "（无标题）"} · {review.publicValue.description || "（无描述）"}</p>}
    {review.status === "ready" && canManage ? <>
      {review.kind === "task_content" ? <>
        <label>拟发布标题<input value={title} maxLength={300} onChange={(event) => setTitle(event.target.value)} /></label>
        <label>拟发布描述<textarea value={description} maxLength={3000} onChange={(event) => setDescription(event.target.value)} /></label>
      </> : <>
        <label>拟发布正文<textarea value={content} maxLength={review.kind === "task_comment" ? 3000 : 40960} onChange={(event) => setContent(event.target.value)} /></label>
        {review.kind === "project_progress" && <label>健康度<select value={health} onChange={(event) => setHealth(event.target.value)}>
          <option value="on_track">正常</option><option value="at_risk">有风险</option><option value="off_track">偏离计划</option>
        </select></label>}
      </>}
      <div className="pa-actions">
        <button disabled={busy} onClick={() => void onDecide("publish", review.kind === "task_content" ? { title, description } : { content, ...(review.kind === "project_progress" ? { health } : {}) })}>审阅并发布</button>
        <button disabled={busy} onClick={() => void onDecide("reject", {})}>驳回</button>
      </div>
    </> : <p className="pa-prose">{review.kind === "task_content" ? `${review.proposal.title || ""}\n${review.proposal.description || ""}` : review.proposal.content || ""}</p>}
    {review.status === "staged" || review.status === "needs_review" ? <p role="status">业务写入结果尚未核清，暂不能发布。</p> : null}
    {["needs_review", "staged"].includes(review.status) && canManage && <button disabled={busy} onClick={() => void onDecide("reconcile", {})}>核对发布结果</button>}
  </article>;
}

function AgentDetail({
  detail: d,
  busy,
  onSource,
  onDecision,
  onConfirm,
  onUndo,
  deliveryReviews,
  deliveryError,
  onVerifyDelivery,
  customerReviews,
  customerReviewError,
  onCustomerReview,
  stepOffset,
  onStepPage,
}: {
  detail: ResponsibilityDetail;
  busy: boolean;
  onSource: (content: string, title: string, url: string) => Promise<unknown>;
  onDecision: (id: string, answer: string) => Promise<unknown>;
  onConfirm: (id: string, evidence: string) => Promise<unknown>;
  onUndo: (id: string) => Promise<unknown>;
  deliveryReviews: AgentDeliveryReview[];
  deliveryError?: string;
  onVerifyDelivery: (review: AgentDeliveryReview, messageId: string) => Promise<unknown>;
  customerReviews: AgentCustomerReview[];
  customerReviewError?: string;
  onCustomerReview: (review: AgentCustomerReview, decision: "publish" | "reject" | "reconcile", edited: Record<string, string>) => Promise<unknown>;
  stepOffset: number;
  onStepPage: (offset: number) => void;
}) {
  const reviewForAction = (actionId: string | null) => customerReviews.find(review => review.sourceActionId === actionId);
  return (
    <div className="pa-sections">
      <details>
        <summary>运行费用与用量</summary>
        <p>每次模型费用上限：{(d.responsibility.budget.maxCostUsdPerRun ?? 1).toFixed(2)} 美元</p>
        <p className="work-meta">费用为 SDK 用量回报的估算，不是供应商账单；旧记录没有费用时显示未记录。</p>
        {d.runs.map((r) => <p key={r.id}>{time(r.startedAt)} · {r.costUsd == null ? "费用未记录" : r.usageComplete === true ? `模型费用 ${r.costUsd.toFixed(4)} 美元（SDK 估算）` : r.costUsd > 0 ? `已观察费用至少 ${r.costUsd.toFixed(4)} 美元，总费用未知（未取得完整用量回执）` : "费用未知（未取得完整用量回执）"}</p>)}
      </details>
      <section>
        <h3>群消息送达核查</h3>
        {deliveryError && <p role="alert">{deliveryError}</p>}
        {!deliveryReviews.length && !deliveryError && (
          <p className="work-meta">目前没有发送结果不明的群消息。</p>
        )}
        {deliveryReviews.map((review) => (
          <article className="pa-record" key={`${review.kind}:${review.id}`}>
            <h4>{review.title}</h4>
            <p>{review.reason}</p>
            <p className="work-meta">{time(review.createdAt)} · 群 {review.channelId}</p>
            {d.canManage ? (
              <TextAction
                label="实际飞书消息 ID"
                button="读取远端核查送达"
                busy={busy}
                onSubmit={(messageId) => onVerifyDelivery(review, messageId)}
              />
            ) : <p>等待负责人读取远端消息核查。</p>}
          </article>
        ))}
      </section>
      <section>
        <h3>待人工决定</h3>
        {d.decisions.filter((x) => x.status === "open").length === 0 && (
          <p className="work-meta">目前没有待决定事项。</p>
        )}
        {d.decisions.map((x) => (
          <article className="pa-record" key={x.id}>
            <h4>{x.question}</h4>
            <p>{x.reason}</p>
            {x.status === "resolved" ? (
              <p>已决定：{x.answer}</p>
            ) : d.canManage ? (
              <TextAction
                label="你的决定"
                choices={x.options}
                button="提交决定"
                busy={busy}
                onSubmit={(answer) => onDecision(x.id, answer)}
              />
            ) : (
              <p>等待负责人决定。可选项：{x.options.join("、")}</p>
            )}
          </article>
        ))}
      </section>
      <section>
        <h3>客户项目内容审阅</h3>
        {customerReviewError && <p role="alert">{customerReviewError}</p>}
        {!customerReviews.length && !customerReviewError && <p className="work-meta">目前没有待审内容。</p>}
        {customerReviews.map((review) => (
          <CustomerReviewCard
            key={review.id}
            review={review}
            busy={busy}
            canManage={d.canManage}
            onDecide={(decision, edited) => onCustomerReview(review, decision, edited)}
          />
        ))}
      </section>
      <section>
        <h3>完成证据</h3>
        {d.responsibility.completionCriteria.map((c) => {
          const e = d.evidence.find((x) => x.criterionId === c.id);
          return (
            <article className="pa-record" key={c.id}>
              <h4>{c.description}</h4>
              <p>
                {criterionLabels[c.kind]} ·{" "}
                {e?.satisfied ? "已满足" : "尚未满足"}
              </p>
              <p>{e?.reason || "尚无核验证据"}</p>
              {e?.sourceUrl && safeUrl(e.sourceUrl) && (
                <a href={e.sourceUrl}>核对依据</a>
              )}
              {c.kind === "human_confirmed" && !e?.satisfied && d.canManage && (
                <TextAction
                  label="完成依据"
                  button="确认此条件完成"
                  busy={busy}
                  onSubmit={(evidence) => onConfirm(c.id, evidence)}
                />
              )}
            </article>
          );
        })}
      </section>
      <section>
        <h3>输入与来源</h3>
        {d.sources.length === 0 && <p className="work-meta">尚无补充来源。</p>}
        {d.sources.map((s) => (
          <article className="pa-record" key={s.id} id={`source-${s.id}`}>
            <h4>{s.title}</h4>
            <p className="pa-prose">{s.content}</p>
            <p className="work-meta">{time(s.observedAt)}</p>
            {s.url && safeUrl(s.url) && <a href={s.url}>查看来源</a>}
          </article>
        ))}
        <SourceForm busy={busy} onSubmit={onSource} />
      </section>
      <section>
        <h3>项目记忆</h3>
        {!d.memory.length && <p className="work-meta">尚无项目记忆。</p>}
        {d.memory.map((m) => (
          <article key={m.id} className="pa-record">
            <p>{m.content}</p>
            <p className="work-meta">
              {
                {
                  fact: "事实",
                  commitment: "承诺",
                  dependency: "依赖",
                  open_question: "待澄清",
                }[m.kind]
              }{" "}
              · {time(m.updatedAt)}
            </p>
            <SourceRefs ids={m.sourceIds} detail={d} />
          </article>
        ))}
      </section>
      <section>
        <h3>历史草稿（只读）</h3>
        {!d.artifacts.length && <p className="work-meta">没有历史草稿。v4 不再创建草稿。</p>}
        {d.artifacts.map((a) => (
          <article key={a.id} className="pa-record">
            <h4>{a.title}</h4>
            <p className="pa-prose">{a.content}</p>
            <SourceRefs ids={a.sourceIds} detail={d} />
          </article>
        ))}
      </section>
      <section>
        <h3>行动与核对记录</h3>
        <p className="work-meta">共 {d.stepsTotal ?? d.steps.length} 条，当前显示第 {stepOffset + 1} 至 {stepOffset + d.steps.length} 条</p>
        {!d.steps.length && (
          <p className="work-meta">尚无行动记录。立即检查会排入队列。</p>
        )}
        {d.steps.map((s) => (
          <article className="pa-record" key={s.id}>
            <h4>
              {s.tool
                ? (Object.hasOwn(toolLabels, s.tool) ? toolLabels[s.tool as AgentToolName] : "未知工具行动")
                : {
                    observation: "观察",
                    plan: "计划",
                    tool: "行动",
                    wait: "等待",
                    decision: "人工决定",
                    completion: "完成检查",
                    error: "错误",
                  }[s.kind]}
            </h4>
            <p>{s.reason}</p>
            <p className="work-meta">
              {
                {
                  pending: "待执行",
                  sending: "执行中",
                  applied: "已执行",
                  failed: "失败",
                  needs_review: "待核对",
                }[s.status]
              }{" "}
              · {time(s.createdAt)}
            </p>
            {d.canManage && s.actionId && s.status === "applied" && reversibleAgentTools.has(s.tool ?? "") && (!reviewForAction(s.actionId) || reviewForAction(s.actionId)?.status === "ready") && (
              <button disabled={busy} onClick={() => void onUndo(s.actionId!)}>
                {reviewForAction(s.actionId)?.status === "ready" ? "撤回待审草稿" : "撤销此内部动作"}
              </button>
            )}
            {s.actionId && reviewForAction(s.actionId) && reviewForAction(s.actionId)?.status !== "ready" && <p>客户内容已发布、已驳回或结果待核查，不能用 AI 撤销；请在上方客户内容审阅中核对。</p>}
            <details>
              <summary>核对输入与结果</summary>
              <pre>
                {JSON.stringify(
                  { input: s.input, output: s.output, actionId: s.actionId },
                  null,
                  2,
                )}
              </pre>
            </details>
          </article>
        ))}
        <div className="pa-actions">
          <button disabled={stepOffset <= 0} onClick={() => onStepPage(Math.max(0, stepOffset - 40))}>较新的记录</button>
          <button disabled={d.stepsNextOffset == null} onClick={() => onStepPage(d.stepsNextOffset ?? stepOffset)}>较早的记录</button>
        </div>
      </section>
      <details>
        <summary>检查历史与资源限制</summary>
        {d.runs.map((r) => (
          <article className="pa-record" key={r.id}>
            <p>
              {time(r.startedAt)} ·{" "}
              {
                {
                  queued: "已排队",
                  running: "运行中",
                  waiting: "等待",
                  completed: "本次检查完成",
                  failed: "失败",
                  needs_review: "待核对",
                  stopped: "已停止",
                }[r.status]
              }
            </p>
            <p>{r.summary || "暂无摘要"}</p>
            {r.error && <p role="alert">{r.error}</p>}
            <p className="work-meta">
              行动 {r.steps} 次 · 写入 {r.writes} 次 · 模型用量 {r.tokens}
            </p>
          </article>
        ))}
        <details>
          <summary>引擎与会话核对明细</summary>
          <p>执行引擎：{d.engine || "旧记录未标注"}</p>
          <pre>{JSON.stringify({ session: d.session ?? null, runs: d.runs.map((r) => ({ runId: r.id, engine: r.engine, sessionId: r.sessionId, sdkVersion: r.sdkVersion })) }, null, 2)}</pre>
        </details>
        <p>
          每日最多 {d.responsibility.budget.maxRunsPerDay} 次检查；间隔至少{" "}
          {d.responsibility.budget.minWakeMinutes} 分钟。
        </p>
      </details>
    </div>
  );
}
function SourceRefs({
  ids,
  detail,
}: {
  ids: string[];
  detail: ResponsibilityDetail;
}) {
  return (
    <p className="pa-refs">
      {ids.map((id) => (
        <a key={id} href={`#source-${id}`}>
          {detail.sources.find((s) => s.id === id)?.title || "来源已不可用"}
        </a>
      ))}
    </p>
  );
}
function TextAction({
  label,
  button,
  busy,
  onSubmit,
  choices = [],
}: {
  label: string;
  button: string;
  busy: boolean;
  onSubmit: (text: string) => Promise<unknown>;
  choices?: string[];
}) {
  const [value, setValue] = useState("");
  return (
    <form
      className="work-form"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(value.trim());
      }}
    >
      {choices.length > 0 && (
        <div className="work-toolbar">
          {choices.map((c) => (
            <button key={c} type="button" onClick={() => setValue(c)}>
              {c}
            </button>
          ))}
        </div>
      )}
      <label>
        {label}
        <textarea
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      <button disabled={busy || !value.trim() || (choices.length > 0 && !choices.includes(value.trim()))}>{button}</button>
    </form>
  );
}
function SourceForm({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (content: string, title: string, url: string) => Promise<unknown>;
}) {
  const [title, setTitle] = useState(""),
    [content, setContent] = useState(""),
    [url, setUrl] = useState("");
  return (
    <form
      className="work-form"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(content.trim(), title.trim(), url).then((success) => {
          if (success === true) {
            setTitle("");
            setContent("");
            setUrl("");
          }
        });
      }}
    >
      <h4>补充输入</h4>
      <label>
        来源标题
        <input
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <label>
        事实、纪要或新进展
        <textarea
          required
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </label>
      <label>
        来源链接（可选）
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>
      <button disabled={busy || !title.trim() || !content.trim()}>
        补充输入
      </button>
    </form>
  );
}

function CriterionEntity({
  kind,
  projectId,
  artifacts,
  value,
  onChange,
}: {
  kind: CompletionCriterion["kind"];
  projectId: string;
  artifacts: { id: string; title: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  const entries = useQuery<
    {
      id: string;
      title: string;
      projectId?: string | null;
      form?: Record<string, unknown>;
    }[]
  >({
    queryKey: ["hub", "criterion-entities", kind, projectId],
    enabled: !!projectId && kind !== "artifact_exists",
    queryFn: () =>
      kind === "task_done" ? listTasks() : listApprovals(""),
  });
  const options =
    kind === "artifact_exists"
      ? artifacts
      : (entries.data || []).filter(
          (x) => x.projectId === projectId || x.form?.projectId === projectId,
        );
  const name =
    kind === "task_done"
      ? "任务"
      : kind === "approval_approved"
        ? "审批"
        : "草稿";
  return (
    <div>
      <label>
        关联{name}
        <select
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">请选择本项目{name}</option>
          {value && !options.some((x) => x.id === value) && (
            <option value={value}>当前关联记录（暂未载入）</option>
          )}
          {options.map((x) => (
            <option key={x.id} value={x.id}>
              {x.title}
            </option>
          ))}
        </select>
      </label>
      {entries.isFetching && <p role="status">正在加载{name}…</p>}
      {entries.error && (
        <p role="alert">
          {entries.error.message}{" "}
          <button type="button" onClick={() => void entries.refetch()}>
            重试
          </button>
        </p>
      )}
      {!entries.isFetching && !entries.error && !options.length && (
        <p className="work-meta">
          暂无可选{name}。可先用人工确认，保存职责并准备资料后再修改条件。
        </p>
      )}
      <details>
        <summary>通过记录编号关联</summary>
        <label>
          {name}记录编号
          <input value={value} onChange={(e) => onChange(e.target.value)} />
        </label>
        <p className="work-meta">服务器仍会核对记录所属项目及完成状态。</p>
      </details>
    </div>
  );
}
